import type { ReactNode } from 'react';

/**
 * One icon control in a header bar — extracted in TROOP-122 so `AppHeader`, `AppBar` and
 * `AccountMenu` share a single tap target rather than three near-copies of one.
 *
 * `--tap-min` square, no border, inheriting the bar's ink so it works on the inverse surface.
 */
export function HeaderButton({
  label,
  onClick,
  badge,
  badgeNoun = 'unread',
  expanded,
  children,
}: {
  label: string;
  onClick: () => void;
  /** Unread behind this destination. The dot is the signal; the number is for screen readers. */
  badge?: number;
  /**
   * What the badge counts, for the label: "Conversations, 3 unread", "Calendar, 2 new". The home
   * icon counts new announcements and events (TROOP-268), which are not unread messages, and a
   * label calling them that would be the badge meaning something it does not.
   */
  badgeNoun?: string;
  /** Present only on a control that opens a menu rather than going somewhere. */
  expanded?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={badge && badge > 0 ? `${label}, ${badge} ${badgeNoun}` : label}
      aria-haspopup={expanded === undefined ? undefined : 'menu'}
      aria-expanded={expanded}
      title={label}
      className="relative inline-flex items-center justify-center rounded-md shrink-0"
      style={{
        minHeight: 'var(--tap-min)',
        minWidth: 'var(--tap-min)',
        background: 'none',
        border: 'none',
        color: 'inherit',
      }}
    >
      {children}
      {badge !== undefined && badge > 0 && (
        // Positioned over the icon rather than beside it, so the header does not reflow when a
        // message arrives. aria-hidden because the button's own label already carries the count --
        // the dot's own role="status" would otherwise announce it twice.
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 'calc(50% - 12px)',
            right: 'calc(50% - 12px)',
            width: 8,
            height: 8,
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-accent-announcement)',
          }}
        />
      )}
    </button>
  );
}
